// TaskGroupDetailComponent.js
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { BASE_URL } from "utils/constant";
import AddTaskModal from "./AddTaskModal";
import EditNameModal from "./EditNameModal";
import CalendarPicker from "./CalenderPicker";
import { useAlert } from "components/UI_components/Alert";
import { format } from "date-fns";
import { MdDelete, MdEdit } from "react-icons/md";
import MemberAvatar from "components/tasks/MemberAvatar";
import { getMemberKey } from "utils/tasks/taskHelpers";
import DeleteConfirmationModal from "../Snags/components/DeleteConfirmationModal";

/* ---------- small helpers ---------- */
const priorityText = (n) =>
  ({ 1: "High", 2: "Medium", 3: "Low" })[n] || "Medium";

const todayISO = () => new Date().toISOString().split("T")[0];

const toArray = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw?.task_groups) return raw.task_groups;
  if (raw?.tasks) return raw.tasks;
  if (raw?.data?.results) return raw.data.results;
  if (raw?.data) return raw.data;
  return [];
};

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.split(" ");
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return parts[0].charAt(0).toUpperCase() + parts[1].charAt(0).toUpperCase();
};

const TaskGroupDetailComponent = ({
  taskGroup,
  onClose,
  onTaskGroupDeleted,
  refetchTaskGroups,
  onGroupEditStart, // ← add
  onGroupEditSubmit,
  updateGroupNameLocally,
  isVisible = true,
}) => {
  const { addAlert } = useAlert();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  /* ---------- auth ---------- */
  const token = localStorage.getItem("access");
  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token],
  );
  /* ---------- state ---------- */
  const [activeTab, setActiveTab] = useState("Tasks");
  const [showOptions, setShowOptions] = useState(false);
  const optionsButtonRef = useRef(null);
  const optionsDropdownRef = useRef(null);
  const [tasks, setTasks] = useState([]); // fetched tasks (+sub-counts)
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(taskGroup.startDate || null);
  const [endDate, setEndDate] = useState(taskGroup.endDate || null);
  const [isUpdatingDates, setIsUpdatingDates] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [, setIsDeleting] = useState(false);
  const [, setIsSaving] = useState(false);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [editedName, setEditedName] = useState(taskGroup.name || "");
  const [displayName, setDisplayName] = useState(taskGroup.name || "");
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [loadingSubtaskId, setLoadingSubtaskId] = useState(null);
  const [siteMembers, setSiteMembers] = useState([]);
  const [isGroupLinked, setIsGroupLinked] = useState(false);

  const [hasTaskAuthority, setHasTaskAuthority] = useState(false);
  /* Fetch task group details to check if it's linked to BoQ */
  const fetchTaskGroupDetails = useCallback(async () => {
    try {
      const siteData = JSON.parse(localStorage.getItem("siteData") || "{}");
      const siteId = siteData?.id || localStorage.getItem("site");
      if (!siteId) return;

      const response = await fetch(
        `${BASE_URL}/task/get-all-task-groups/?site_id=${siteId}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await response.json();
      const taskGroups = Array.isArray(data) ? data : data?.data || [];
      const currentGroup = taskGroups.find((tg) => tg.id === taskGroup.id);
      if (currentGroup) {
        setIsGroupLinked(currentGroup.is_group_linked === true);
      }
    } catch (error) {
      console.error("Error fetching task group details:", error);
      setIsGroupLinked(false);
    }
  }, [taskGroup.id, token]);

  useEffect(() => {
    const fetchSiteMembersForAuthority = async () => {
      try {
        const siteData = JSON.parse(localStorage.getItem("siteData") || "{}");
        const siteId = siteData?.id || localStorage.getItem("site");
        if (!siteId) return;
        const res = await fetch(
          `${BASE_URL}/team/list-site-members/${siteId}/`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data = await res.json();
        const members = Array.isArray(data) ? data : data?.data || [];
        const currentMember = members.find((m) => m.user_id === user.user);

        setSiteMembers(Array.isArray(members) ? members : []);
        setHasTaskAuthority(
          currentMember?.create_taskgroup ||
            currentMember?.is_admin ||
            currentMember?.joined_as === "owner" ||
            false,
        );
      } catch {
        setSiteMembers([]);
        setHasTaskAuthority(false);
      }
    };
    fetchSiteMembersForAuthority();
  }, [token, user.user]);

  /* ---------- fetch tasks & sub-counts ---------- */
  const fetchTasksForGroup = useCallback(async (tgId) => {
    setLoading(true);
    try {
      // 1️⃣  grab tasks for this task-group
      const resT = await fetch(`${BASE_URL}/task/get-all-tasks/`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ task_group: tgId }),
      });
      if (!resT.ok) throw new Error();
      const tArr = toArray(await resT.json());

      // 2️⃣  for each task, fetch its subtasks just to count them
      const tasksWithCounts = await Promise.all(
        tArr.map(async (t) => {
          const taskId = t.task_id ?? t.id;
          let subCount = 0;
          try {
            const resS = await fetch(`${BASE_URL}/task/get-all-subtasks/`, {
              method: "POST",
              headers: authHeaders,
              body: JSON.stringify({ task_id: taskId }),
            });
            if (resS.ok) subCount = toArray(await resS.json()).length;
          } catch {
            /* ignore sub-fetch error; leave subCount = 0 */
          }
          return {
            id: taskId,
            name: t.task_name ?? t.name ?? "(no-title)",
            status: t.status || "Ongoing",
            priority: priorityText(t.priority || 2),
            dueDate: t.end_date || "",
            subCount,
            expanded: false,
            subtasks: [],
          };
        }),
      );

      setTasks(tasksWithCounts);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchTasksForGroup(taskGroup.id);
  }, [fetchTasksForGroup, taskGroup.id]);

  useEffect(() => {
    fetchTaskGroupDetails();
  }, [fetchTaskGroupDetails]);

  // Fetch subtasks for a single task
  const fetchSubtasksForTask = async (taskId) => {
    try {
      setLoadingSubtaskId(taskId);
      const res = await fetch(`${BASE_URL}/task/get-all-subtasks/`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ task_id: taskId }),
      });
      if (!res.ok) throw new Error();
      const list = toArray(await res.json());
      const subs = list.map((st) => ({
        id: st.subtask_id ?? st.id,
        name: st.subtask_name ?? st.name ?? "(no-title)",
        status: st.is_completed ? "Completed" : "Ongoing",
        priority: priorityText(st?.priority || 2),
        dueDate: st.end_date || "",
      }));
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, subtasks: subs } : t)),
      );
    } catch (e) {
      console.error("Couldn't load subtasks", e);
      addAlert("Failed to load subtasks.", "error");
    } finally {
      setLoadingSubtaskId(null);
    }
  };

  const toggleTaskExpand = (taskId) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, expanded: !t.expanded } : t)),
    );
    const target = tasks.find((t) => t.id === taskId);
    if (target && !target.expanded && (target.subtasks?.length || 0) === 0) {
      fetchSubtasksForTask(taskId);
    }
  };

  /* ---------- handlers for delete and edit ---------- */
  const handleDeleteTaskGroup = async () => {
    setIsDeleting(true);
    try {
      await fetch(
        `${BASE_URL}/task/taskgroup/delete/${taskGroup.id}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // if (!response.ok) {
      //   throw new Error("Failed to delete task group");
      // }

      // Close modal and notify parent component
      setDeleteConfirmOpen(false);
      // onTaskGroupDeleted(taskGroup.id);
      refetchTaskGroups(); // Refresh task groups in parent component

      onClose();
    } catch (error) {
      console.error("Error deleting task group:", error);
      addAlert("Failed to delete task group. Please try again.", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  // const handleDateChange = (e) => {
  //   const { name, value } = e.target;
  //   setEditedDates((prev) => ({
  //     ...prev,
  //     [name]: value,
  //   }));
  // };

  const handleDateChange = async (newStartDate, newEndDate) => {
    setIsUpdatingDates(true);
    try {
      const res = await fetch(
        `${BASE_URL}/task/task-group/${taskGroup.id}/update`,
        {
          method: "PUT",
          headers: authHeaders,
          body: JSON.stringify({
            start_date: newStartDate
              ? format(newStartDate, "yyyy-MM-dd")
              : null,
            end_date: newEndDate ? format(newEndDate, "yyyy-MM-dd") : null,
          }),
        },
      );

      const data = await res.json();
      if (data.detail) {
        addAlert(
          "You do not have permission to update this task group name.",
          "error",
        );
        return;
      }

      setStartDate(newStartDate);
      setEndDate(newEndDate);
      refetchTaskGroups(); // Refresh task groups in parent component
      addAlert("Dates updated successfully", "success");
    } catch (e) {
      console.error("Update dates failed", e);
      addAlert("Could not update dates.", "error");
    } finally {
      setIsUpdatingDates(false);
    }
  };

  const handleSaveName = async () => {
    if (!editedName.trim()) return;
    setIsSaving(true);
    try {
      const res = await fetch(
        `${BASE_URL}/task/task-group/${taskGroup.id}/update`,
        {
          method: "PUT",
          headers: authHeaders,
          body: JSON.stringify({ name: editedName.trim() }),
        },
      );
      const data = await res.json();
      if (data.detail) {
        addAlert(
          "You do not have permission to update this task group name.",
          "error",
        );
        return;
      }
      setDisplayName(editedName.trim());
      updateGroupNameLocally(taskGroup.id, editedName.trim());
      setShowEditNameModal(false);
      addAlert("Task group name updated", "success");
    } catch {
      addAlert("Could not update name.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddTask = async (taskData) => {
    if (!taskData.name) return;
    try {
      const res = await fetch(`${BASE_URL}/task/create-tasks/`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          task_group: taskGroup.id,
          task_name: taskData.name.trim(),
          task_details: taskData.task_details || "–",
          start_date: taskData.start_date || todayISO(),
          end_date: taskData.end_date || todayISO(),
          priority: taskData.priority || 2,
          estimated_amount: taskData.estimated_amount || 0,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status === false) throw new Error();

      // Refresh tasks after adding new task
      fetchTasksForGroup(taskGroup.id);
      setShowAddTaskModal(false);
    } catch (error) {
      console.error("Failed to create task:", error);
      addAlert("Could not create task. Please try again.", "error");
    }
  };

  /* ---------- static UI assets ---------- */
  const tabs = [
    "Tasks",
    // "Dependency", "Approval", "Files", "Comments"
  ];
  const options = [
    // { id: "colour", label: "Colour", icon: "●", color: "#4caf50" },
    // { id: "star", label: "Star", icon: "★" },
    // { id: "pin", label: "Pin", icon: "📌" },
    // { id: "archive", label: "Archive", icon: "📁" },
    // { id: "edit", label: "Edit Dates", icon: "📅" },
    {
      id: "delete",
      label: "Delete",
      icon: <MdDelete size={24} color="black" />,
      color: "#f44336",
    },
    {
      id: "edit",
      label: "Edit Name",
      icon: <MdEdit size={24} color="black" />,
    },
  ];
  /* ---------- render helpers ---------- */
  const toggleOptions = () => setShowOptions((o) => !o);

  // Close the options dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showOptions) return;
      const clickedInsideDropdown = optionsDropdownRef.current?.contains(
        event.target,
      );
      const clickedOnButton = optionsButtonRef.current?.contains(event.target);
      if (!clickedInsideDropdown && !clickedOnButton) {
        setShowOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showOptions]);

  const handleOptionClick = (optionId) => {
    setShowOptions(false);

    if (optionId === "delete") {
      setDeleteConfirmOpen(true);
    } else if (optionId === "edit") {
      onGroupEditStart?.(taskGroup);
      setEditedName(displayName);
      setShowEditNameModal(true);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "Tasks":
        return (
          <div className="border rounded m-4">
            {loading ? (
              <div className="p-4 text-sm text-gray-500">Loading…</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="p-2">Tasks</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Priority</th>
                    <th className="p-2">Due date</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((t) => (
                    <React.Fragment key={t.id}>
                      <tr className="border-b">
                        <td className="p-2">
                          <div className="flex items-center">
                            <button
                              className="mr-1"
                              onClick={() => toggleTaskExpand(t.id)}
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                            <div className="flex flex-col">
                              <span className="text-sm font-medium">
                                {t.name}
                              </span>
                              {t.subCount > 0 && (
                                <div className="mt-1">
                                  <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs">
                                    {t.subCount} subtasks
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-2">
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              t.status === "Ongoing"
                                ? "bg-yellow-100 text-yellow-800"
                                : t.status === "Completed"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-200 text-gray-800"
                            }`}
                          >
                            {t.status}
                          </span>
                        </td>
                        <td className="p-2">
                          <span className="px-2 py-1 rounded bg-red-100 text-red-800 text-xs">
                            {t.priority}
                          </span>
                        </td>
                        <td className="p-2">{t.dueDate || "-"}</td>
                      </tr>
                      {t.expanded && (
                        <>
                          {loadingSubtaskId === t.id ? (
                            <tr className="border-b">
                              <td
                                className="p-2 pl-8 text-sm text-gray-500"
                                colSpan={4}
                              >
                                Loading subtasks…
                              </td>
                            </tr>
                          ) : t.subtasks && t.subtasks.length > 0 ? (
                            t.subtasks.map((st) => (
                              <tr className="border-b bg-gray-50" key={st.id}>
                                <td className="p-2 pl-8">
                                  <div className="flex items-center pl-5">
                                    <span className="text-sm">{st.name}</span>
                                  </div>
                                </td>
                                <td className="p-2">
                                  <span
                                    className={`px-2 py-1 rounded text-xs ${
                                      st.status === "Ongoing"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : st.status === "Completed"
                                          ? "bg-green-100 text-green-800"
                                          : "bg-gray-200 text-gray-800"
                                    }`}
                                  >
                                    {st.status}
                                  </span>
                                </td>
                                {/* Priority column REMOVED for subtasks */}
                                <td className="p-2"></td>
                                <td className="p-2">{st.dueDate || "-"}</td>
                              </tr>
                            ))
                          ) : (
                            <tr className="border-b">
                              <td
                                className="p-2 pl-8 text-sm text-gray-500"
                                colSpan={4}
                              >
                                No subtasks
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );

      /* ---- other tabs stay as-is ---- */
      case "Dependency":
        return (
          <div className="p-4">
            <p className="text-sm mb-4">Create task group dependency</p>
            <div className="border rounded p-6 flex items-center justify-center">
              <button className="border px-3 py-1 text-sm rounded mr-4">
                + Add previous
              </button>
              <div className="border-2 border-green-500 px-4 py-2 text-center bg-white">
                {taskGroup.name}
              </div>
              <button className="border px-3 py-1 text-sm rounded ml-4">
                + Add after
              </button>
            </div>
          </div>
        );
      default:
        return <div className="p-4">Content not available</div>;
    }
  };

  /* ---------- main render ---------- */
  return (
    <div
      className={`fixed inset-y-0 right-0 w-1/2 z-[100] bg-white shadow-lg border-l-[1px] border-[#A3A3A3] flex flex-col transition-transform duration-300 ease-in-out ${
        isVisible ? "translate-x-0" : "translate-x-full"
      }`}
    >
      {/* header */}
      <div className="flex justify-between items-center px-4 py-3">
        <div>
          {/* <h2 className="text-xl font-semibold">{taskGroup.name}</h2> */}
          <h2
            className={`text-xl font-semibold ${hasTaskAuthority ? "cursor-pointer hover:text-purple-700" : "cursor-default"}`}
            onClick={() => {
              if (hasTaskAuthority) {
                onGroupEditStart?.(taskGroup);
                setEditedName(displayName);
                setShowEditNameModal(true);
              }
            }}
            title={hasTaskAuthority ? "Click to edit name" : ""}
          >
            {displayName}
          </h2>
          <p className="text-sm text-gray-600">
            By {taskGroup.author || "Unknown"}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {/* <button
            className="p-1 rounded hover:bg-gray-100"
            onClick={toggleOptions}
            ref={optionsButtonRef}
          >
            <svg
              className="w-6 h-6 text-gray-900 hover:text-gray-900"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 5v.01M12 12v.01M12 19v.01"
              />
            </svg>
          </button> */}
          {hasTaskAuthority && (
            <button
              className="p-1 rounded hover:bg-gray-100"
              onClick={toggleOptions}
              ref={optionsButtonRef}
            >
              <svg
                className="w-6 h-6 text-gray-900 hover:text-gray-900"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 5v.01M12 12v.01M12 19v.01"
                />
              </svg>
            </button>
          )}
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* quick info strip */}
      <div className="grid grid-cols-3 gap-4 p-4">
        {/* Members Card */}
        <div className="flex flex-col items-start gap-2 border-[1px] border-btn-bg px-3 py-3 rounded">
          <div className="text-sm text-gray-600">Members</div>
          <div className="flex items-center flex-row space-x-2 text-white">
            {(Array.isArray(taskGroup?.allocated_members)
              ? taskGroup.allocated_members
              : []
            )
              .slice(0, 4)
              .map((a, index) => (
                <MemberAvatar
                  key={getMemberKey(a, index)}
                  member={a}
                  siteMembers={siteMembers}
                  getInitials={getInitials}
                  className="w-8 h-8 rounded-full border-1 border-white"
                  imageClassName="object-cover"
                  fallbackClassName="flex items-center justify-center bg-[#343C6A] text-white"
                  style={{
                    marginLeft: index > 0 ? "-5px" : "0px",
                  }}
                />
              ))}
            {(Array.isArray(taskGroup?.allocated_members)
              ? taskGroup.allocated_members.length
              : 0) > 4 ? (
              <div
                style={{
                  marginLeft: "-5px",
                }}
                className="w-8 h-8 rounded-full border-1 border-white flex text-white items-center justify-center bg-[#343C6A]"
              >
                +{taskGroup.allocated_members.length - 4}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex flex-col border-[1px] border-[#3B0069] rounded-lg px-3 py-3">
          <div className="text-sm text-gray-600">Date Range</div>
          <div className="relative">
            <CalendarPicker
              startDate={startDate}
              endDate={endDate}
              disabled={true}
              onDateChange={handleDateChange}
              placeholder="Select dates"
            />
            {isUpdatingDates && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* tabs */}
      <div className="flex border-b">
        {tabs.map((t) => (
          <button
            key={t}
            className={`px-8 py-2 text-sm ${
              activeTab === t
                ? "text-purple-600 border-b-2 border-purple-600"
                : "text-gray-600"
            }`}
            onClick={() => setActiveTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {/* tab content */}
      <div className="flex-1 overflow-auto">{renderTabContent()}</div>

      {/* options dropdown */}
      {showOptions && (
        <div
          className="absolute top-12 right-12 bg-white border rounded shadow-lg w-48 z-10"
          ref={optionsDropdownRef}
        >
          {options.map((opt) => (
            <div
              key={opt.id}
              className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
              onClick={() => handleOptionClick(opt.id)}
            >
              <span className="mr-2" style={{ color: opt.color || "inherit" }}>
                {opt.icon}
              </span>
              <span className={opt.color === "#f44336" ? "text-red-600" : ""}>
                {opt.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteConfirmOpen}
        title="Delete Task Group"
        message={
          isGroupLinked
            ? `This will also delete BoQ Section: ${taskGroup.name}\nAre you sure you want to proceed? This action cannot be undone.`
            : `Are you sure you want to delete the task group "${taskGroup.name}"? This action cannot be undone.`
        }
        onConfirm={handleDeleteTaskGroup}
        onCancel={() => setDeleteConfirmOpen(false)}
        requireConfirmationText={isGroupLinked ? taskGroup.name : null}
        isBoQLinked={isGroupLinked}
        itemType="Task Group"
        itemName={taskGroup.name}
      />

      <EditNameModal
        isOpen={showEditNameModal}
        title="Edit Task Group Name"
        placeholder="Enter task group name"
        initialValue={editedName}
        onClose={() => {
          setShowEditNameModal(false);
          setEditedName(displayName);
        }}
        onSave={handleSaveName}
      />

      {/* Add Task Modal */}
      {showAddTaskModal && (
        <AddTaskModal
          onClose={() => setShowAddTaskModal(false)}
          handleSubmit={handleAddTask}
          taskGroupId={taskGroup.id}
        />
      )}
    </div>
  );
};

export default TaskGroupDetailComponent;
